import { MathUtils, PerspectiveCamera, Vector2, Vector3 } from "three";
import { Viewer } from "../main";

export interface CustomMouseEvent extends MouseEvent {
  isLeftClick: boolean
  isRightClick: boolean
  mouseDown: boolean
  mouseMoved: boolean
}

export class MouseControls {
  private viewer: Viewer;
  private camera: PerspectiveCamera;

  public down = false;
  public button = 0;
  public upEnabled = false;

  private phiDelta = 0;
  public thetaDelta = 0;
  public wheelDelta = 0;

  private wheelTimeout = null;
  private wheelDelay = 150;

  private endTimeout = 50;
  private endDelay = 500;

  public rotating = false;
  public zooming = false;
  public scrolling = false;
  public movement = false;
  private moveInterval = null;

  private moveStart = new Vector2();
  private moveEnd = new Vector2();
  private moveDelta = new Vector2();
  private moveDistance = 0;
  private lastMovement = performance.now();

  public minFOV;
  public maxFOV;

  constructor(viewer: Viewer, camera: PerspectiveCamera, minimumFOV, maximumFOV) {
    this.viewer = viewer;
    this.camera = camera;
    this.minFOV = minimumFOV;
    this.maxFOV = maximumFOV;
  }

  get angles(): number[] {
    return [this.thetaDelta, this.phiDelta];
  }

  set angles(values) {
    const [theta, phi] = values;

    if (theta != null) {
      this.thetaDelta = theta;
    }

    if (phi != null) {
      this.phiDelta = phi;
    }
  }

  get lookat() {
    let phiDelta = this.phiDelta + MathUtils.degToRad(90);
    let x = Math.cos(this.thetaDelta) * Math.sin(phiDelta);
    let y = Math.sin(this.thetaDelta) * Math.sin(phiDelta);
    let z = -1 * Math.cos(phiDelta);

    return new Vector3(x,y,z);
  }

  get recentActivity() {
    /** Returns true if movement occured within endDelay milliseconds */
    let now = performance.now();
    let end = this.lastMovement + this.endDelay;
    return now < end;
  }

  set fov(value) {
    let fov = Math.max(this.minFOV,
      Math.min(this.maxFOV, value));

    this.camera.fov = fov;
  }

  getCustomEvent(initialEvent): CustomMouseEvent {
    initialEvent.isLeftClick = this.button === 0;
    initialEvent.isRightClick = this.button === 2;
    initialEvent.mouseDown = this.down;
    initialEvent.mouseMoved = this.movement;
    return initialEvent;
  }

  clearInterval() {
    if (this.moveInterval !== null) {
      clearTimeout(this.moveInterval);
    }
  }

  setInterval(callback) {
    this.moveInterval = setTimeout(() => {
      callback();
    }, this.endTimeout);
  }

  onMouseScroll() {
    this.scrolling = true;
    clearTimeout(this.wheelTimeout);

    this.wheelTimeout = setTimeout(() => {
      this.scrolling = false;
    }, this.wheelDelay);
  }

  onMouseMove(event) {
    if (this.down === false) return;

    this.moveEnd.set(event.clientX, event.clientY);

    const distance = this.moveStart.distanceTo(this.moveEnd);
    this.moveDistance += distance;

    this.moveDelta.subVectors(this.moveEnd, this.moveStart);
    this.moveStart.copy(this.moveEnd);
    this.movement = this.moveDistance > 0;
  }

  updateRotation(fov, width, height, invertX=false, invertY=false) {
    if (this.button !== 0) return;

    let delta = new Vector2().copy(this.moveDelta);
    delta.multiplyScalar(fov / 90);

    if (invertX) {
      delta.x *= -1;
    }

    if (invertY) {
      delta.y *= -1;
    }

    this.rotate(
      2 * Math.PI * delta.x / width,
      Math.PI * delta.y / height
    );

    this.lastMovement = performance.now();
  }

  updateCameraMatrix() {
    const lookat = new Vector3()
      .copy(this.lookat)
      .add(this.camera.position);

    this.camera.lookAt(lookat);
    this.camera.updateProjectionMatrix();
  }

  onMouseWheel(event) {
    this.wheelDelta = 0;
    if (event.deltaY > 0) {
      this.wheelDelta = -5;
    } else if (event.deltaY < 0) {
      this.wheelDelta = 5;
    }

    if (this.wheelDelta !== 0) {
      this.zoom();
    }
  }

  zoom() {
    const fov = this.camera.fov - this.wheelDelta;
    this.camera.fov = Math.max(this.minFOV, Math.min(fov, this.maxFOV));
    this.wheelDelta = 0.0;
    this.lastMovement = performance.now();
  }

  rotate(theta, phi) {
    this.thetaDelta += theta;
    this.phiDelta += phi;
    this.phiDelta = Math.min(this.phiDelta, MathUtils.degToRad(89));
    this.phiDelta = Math.max(this.phiDelta, MathUtils.degToRad(-89));

    const maxAngle = 360 * Math.PI / 180;
    this.thetaDelta = this.thetaDelta % maxAngle;
    this.phiDelta  = this.phiDelta % maxAngle;
  }

  onMouseDown(event) {
    this.down = true;
    this.button = event.button;
    this.moveStart.set(event.clientX, event.clientY);
    this.moveDistance = 0;
    this.upEnabled = true;
  }

  onMouseUp() {
    this.down = false;
  }

  update() {
    this.updateCameraMatrix();
  }

}
